import { PageHeading } from "@/components/ui/page";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useSearchParams } from "react-router-dom";
import { Tag, Gift, Wallet, Star, Plus, Pencil, History, Eye, Search } from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { PermissionGate } from "@/features/auth/components/PermissionGate";
import { usePermission } from "@/hooks/usePermission";
import { Loading } from "@/components/ui/loader";
import { useToast } from "@/hooks/use-toast";
import {
	couponsAPI,
	giftCardsAPI,
	storeCreditAPI,
	loyaltyAPI,
	companyAPI,
	branchesAPI,
	getApiErrorMessage,
	getApiErrorPayload,
} from "@/lib/api";
import { cn, formatNumber, formatDate, formatDateTime } from "@/lib/utils";
import { Price } from "@/components/ui/price";
import { EmptyState } from "@/components/ui/empty-state";
import { useCurrency } from "@/hooks/useCurrency";

type HubType = "coupons" | "giftCards" | "storeCredit" | "loyalty";

const TYPE_STYLES: Record<HubType, { icon: React.ElementType; active: string; dot: string }> = {
	coupons: {
		icon: Tag,
		active: "border-warning/25 bg-warning/10 text-warning",
		dot: "bg-amber-500",
	},
	giftCards: {
		icon: Gift,
		active: "border-primary/25 bg-primary/10 text-primary",
		dot: "bg-violet-500",
	},
	storeCredit: {
		icon: Wallet,
		active: "border-success/25 bg-success/10 text-success",
		dot: "bg-emerald-500",
	},
	loyalty: {
		icon: Star,
		active: "border-sky-300 bg-sky-50 text-sky-800",
		dot: "bg-sky-500",
	},
};

const TYPE_PERMISSIONS: Record<HubType, string> = {
	coupons: "cashierDiscount.read",
	giftCards: "cashierGiftCard.read",
	storeCredit: "cashierStoreCredit.read",
	loyalty: "cashierLoyalty.read",
};

interface CouponRow {
	id: number;
	code: string;
	type: "percent" | "fixed";
	value: number;
	minOrderAmount: number;
	maxUses: number | null;
	usesCount: number;
	perCustomerLimit: number | null;
	branchId: number | null;
	isActive: boolean;
	validFrom: string | null;
	validTo: string | null;
}

interface BranchRow {
	id: number;
	name: string;
	nameEn?: string;
}

interface CouponRedemptionRow {
	id: number;
	saleId: number | null;
	customerId: number | null;
	amountApplied: number;
	createdAt: string;
}

interface GiftCardRow {
	id: number;
	code: string;
	initialBalance: number;
	balance: number;
	status: string;
	expiresAt: string | null;
	createdAt: string;
}

interface GiftCardRedemption {
	id: number;
	saleId: number | null;
	amount: number;
	createdAt: string;
}

interface StoreCreditTx {
	id: number;
	customerId: number;
	type: string;
	amount: number;
	balanceAfter: number;
	notes: string | null;
	createdAt: string;
}

interface LoyaltySettings {
	id?: number;
	earnRatePerCurrency: number;
	redeemValuePerPoint: number;
	isActive: boolean;
}

const EMPTY_COUPON_FORM = {
	code: "",
	type: "percent" as "percent" | "fixed",
	value: "",
	minOrderAmount: "0",
	maxUses: "",
	perCustomerLimit: "",
	branchId: "",
	validFrom: "",
	validTo: "",
	isActive: true,
};

const EMPTY_GIFT_FORM = {
	code: "",
	initialBalance: "",
	expiresAt: "",
};

function toDateInput(value: string | null): string {
	if (!value) return "";
	const d = new Date(value);
	if (Number.isNaN(d.getTime())) return "";
	return d.toISOString().slice(0, 10);
}

function generateGiftCode(): string {
	return `GC-${Date.now().toString(36).toUpperCase().slice(-8)}`;
}

/** Coupons, gift cards, store credit and loyalty merged into one hub; type chips act as the filter. */
export default function CheckoutHubPage() {
	const { t, i18n } = useTranslation();
	const { toast } = useToast();
	const currency = useCurrency();
	const currencyName = t(`currencies.${currency}`, { defaultValue: currency }).replace(/\s*\([^)]*\)\s*$/, "");
	const [searchParams, setSearchParams] = useSearchParams();

	const canCoupons = usePermission(TYPE_PERMISSIONS.coupons);
	const canGiftCards = usePermission(TYPE_PERMISSIONS.giftCards);
	const canStoreCredit = usePermission(TYPE_PERMISSIONS.storeCredit);
	const canLoyalty = usePermission(TYPE_PERMISSIONS.loyalty);
	const canCreateCoupons = usePermission("cashierDiscount.create");
	const canIssueGiftCards = usePermission("cashierGiftCard.create");

	const visibleTypes = useMemo<HubType[]>(() => {
		const types: HubType[] = [];
		if (canCoupons) types.push("coupons");
		if (canGiftCards) types.push("giftCards");
		if (canStoreCredit) types.push("storeCredit");
		if (canLoyalty) types.push("loyalty");
		return types;
	}, [canCoupons, canGiftCards, canStoreCredit, canLoyalty]);

	const paramType = searchParams.get("type") as HubType | null;
	const [type, setType] = useState<HubType>(
		paramType && visibleTypes.includes(paramType) ? paramType : (visibleTypes[0] ?? "coupons"),
	);
	const [search, setSearch] = useState("");
	const [statusFilter, setStatusFilter] = useState("all");

	const switchType = (next: HubType) => {
		setType(next);
		setSearch("");
		setStatusFilter("all");
		setSearchParams(
			prev => {
				const p = new URLSearchParams(prev);
				p.set("type", next);
				return p;
			},
			{ replace: true },
		);
	};

	// --- Coupons ---
	const [coupons, setCoupons] = useState<CouponRow[]>([]);
	const [branches, setBranches] = useState<BranchRow[]>([]);
	const [couponsLoading, setCouponsLoading] = useState(canCoupons);
	const [couponDialogOpen, setCouponDialogOpen] = useState(false);
	const [editingCouponId, setEditingCouponId] = useState<number | null>(null);
	const [couponForm, setCouponForm] = useState(EMPTY_COUPON_FORM);
	const [couponSaving, setCouponSaving] = useState(false);
	const [redemptionsOpen, setRedemptionsOpen] = useState(false);
	const [redemptionsCoupon, setRedemptionsCoupon] = useState<CouponRow | null>(null);
	const [redemptions, setRedemptions] = useState<CouponRedemptionRow[]>([]);
	const [redemptionsLoading, setRedemptionsLoading] = useState(false);

	// --- Gift cards ---
	const [cards, setCards] = useState<GiftCardRow[]>([]);
	const [cardsLoading, setCardsLoading] = useState(canGiftCards);
	const [giftDialogOpen, setGiftDialogOpen] = useState(false);
	const [giftForm, setGiftForm] = useState(EMPTY_GIFT_FORM);
	const [giftSaving, setGiftSaving] = useState(false);
	const [giftDetailOpen, setGiftDetailOpen] = useState(false);
	const [giftDetailLoading, setGiftDetailLoading] = useState(false);
	const [selectedCard, setSelectedCard] = useState<GiftCardRow | null>(null);
	const [giftRedemptions, setGiftRedemptions] = useState<GiftCardRedemption[]>([]);

	// --- Store credit ---
	const [transactions, setTransactions] = useState<StoreCreditTx[]>([]);
	const [txLoading, setTxLoading] = useState(canStoreCredit);
	const [appliedCustomerId, setAppliedCustomerId] = useState<number | undefined>(undefined);

	// --- Loyalty ---
	const [loyalty, setLoyalty] = useState<LoyaltySettings>({
		earnRatePerCurrency: 1,
		redeemValuePerPoint: 0.01,
		isActive: false,
	});
	const [loyaltyLoading, setLoyaltyLoading] = useState(canLoyalty);
	const [loyaltySaving, setLoyaltySaving] = useState(false);
	const [planBlocked, setPlanBlocked] = useState(false);

	const branchLabel = (branchId: number | null) => {
		if (branchId == null) return t("checkoutPromotions.coupons.allBranches");
		const branch = branches.find(b => b.id === branchId);
		if (!branch) return `#${branchId}`;
		return i18n.language === "ar" ? branch.name : branch.nameEn || branch.name;
	};

	const loadCoupons = useCallback(async () => {
		if (!canCoupons) return;
		try {
			setCouponsLoading(true);
			const [data, branchList] = await Promise.all([couponsAPI.list(), branchesAPI.getAll()]);
			setCoupons(Array.isArray(data) ? data : []);
			setBranches(Array.isArray(branchList) ? branchList : []);
		} catch {
			toast({
				title: t("common.error"),
				description: t("checkoutPromotions.coupons.errors.load"),
				variant: "destructive",
			});
		} finally {
			setCouponsLoading(false);
		}
	}, [canCoupons, t, toast]);

	const loadCards = useCallback(async () => {
		if (!canGiftCards) return;
		try {
			setCardsLoading(true);
			const data = await giftCardsAPI.list();
			setCards(Array.isArray(data) ? data : []);
		} catch {
			toast({
				title: t("common.error"),
				description: t("checkoutPromotions.giftCards.errors.load"),
				variant: "destructive",
			});
		} finally {
			setCardsLoading(false);
		}
	}, [canGiftCards, t, toast]);

	const loadTransactions = useCallback(async () => {
		if (!canStoreCredit) return;
		try {
			setTxLoading(true);
			const data = await storeCreditAPI.listTransactions({
				page: 1,
				limit: 50,
				customerId: appliedCustomerId,
			});
			setTransactions(data.transactions ?? []);
		} catch {
			toast({
				title: t("common.error"),
				description: t("checkoutPromotions.storeCredit.errors.load"),
				variant: "destructive",
			});
		} finally {
			setTxLoading(false);
		}
	}, [canStoreCredit, appliedCustomerId, t, toast]);

	const loadLoyalty = useCallback(async () => {
		if (!canLoyalty) return;
		try {
			setLoyaltyLoading(true);
			const [data, subscription] = await Promise.all([
				loyaltyAPI.getSettings(),
				companyAPI.getSubscription().catch(() => null),
			]);
			setPlanBlocked(!subscription?.effectiveFeatures?.loyaltyApps);
			setLoyalty({
				earnRatePerCurrency: Number(data.earnRatePerCurrency ?? 1),
				redeemValuePerPoint: Number(data.redeemValuePerPoint ?? 0.01),
				isActive: Boolean(data.isActive),
				id: data.id,
			});
		} catch (error) {
			const payload = getApiErrorPayload(error);
			if (payload?.key?.includes("notAvailableOnPlan")) {
				setPlanBlocked(true);
				setLoyalty(s => ({ ...s, isActive: false }));
			} else {
				toast({
					title: t("common.error"),
					description: t("checkoutPromotions.loyalty.errors.load"),
					variant: "destructive",
				});
			}
		} finally {
			setLoyaltyLoading(false);
		}
	}, [canLoyalty, t, toast]);

	useEffect(() => {
		loadCoupons();
	}, [loadCoupons]);
	useEffect(() => {
		loadCards();
	}, [loadCards]);
	useEffect(() => {
		loadTransactions();
	}, [loadTransactions]);
	useEffect(() => {
		loadLoyalty();
	}, [loadLoyalty]);

	// --- Coupon handlers ---
	const openCouponCreate = () => {
		setEditingCouponId(null);
		setCouponForm(EMPTY_COUPON_FORM);
		setCouponDialogOpen(true);
	};

	const openCouponEdit = (coupon: CouponRow) => {
		setEditingCouponId(coupon.id);
		setCouponForm({
			code: coupon.code,
			type: coupon.type,
			value: String(coupon.value),
			minOrderAmount: String(coupon.minOrderAmount),
			maxUses: coupon.maxUses != null ? String(coupon.maxUses) : "",
			perCustomerLimit: coupon.perCustomerLimit != null ? String(coupon.perCustomerLimit) : "",
			branchId: coupon.branchId != null ? String(coupon.branchId) : "",
			validFrom: toDateInput(coupon.validFrom),
			validTo: toDateInput(coupon.validTo),
			isActive: coupon.isActive,
		});
		setCouponDialogOpen(true);
	};

	const saveCoupon = async () => {
		if (!couponForm.code.trim()) return;
		try {
			setCouponSaving(true);
			const payload = {
				code: couponForm.code.trim(),
				type: couponForm.type,
				value: Number(couponForm.value) || 0,
				minOrderAmount: Number(couponForm.minOrderAmount) || 0,
				maxUses: couponForm.maxUses ? Number(couponForm.maxUses) : undefined,
				perCustomerLimit: couponForm.perCustomerLimit ? Number(couponForm.perCustomerLimit) : undefined,
				branchId: couponForm.branchId ? Number(couponForm.branchId) : null,
				validFrom: couponForm.validFrom ? new Date(couponForm.validFrom).toISOString() : null,
				validTo: couponForm.validTo ? new Date(couponForm.validTo).toISOString() : null,
				isActive: couponForm.isActive,
			};
			if (editingCouponId != null) {
				const { code: _code, ...updatePayload } = payload;
				await couponsAPI.update(editingCouponId, updatePayload);
			} else {
				await couponsAPI.create(payload);
				toast({ title: t("common.success"), description: t("checkoutPromotions.coupons.created") });
			}
			setCouponDialogOpen(false);
			setCouponForm(EMPTY_COUPON_FORM);
			setEditingCouponId(null);
			await loadCoupons();
		} catch (error) {
			toast({
				title: t("common.error"),
				description: getApiErrorMessage(error, t, t("checkoutPromotions.coupons.errors.save")),
				variant: "destructive",
			});
		} finally {
			setCouponSaving(false);
		}
	};

	const toggleCouponActive = async (coupon: CouponRow) => {
		try {
			await couponsAPI.update(coupon.id, { isActive: !coupon.isActive });
			await loadCoupons();
		} catch (error) {
			toast({
				title: t("common.error"),
				description: getApiErrorMessage(error, t, t("checkoutPromotions.coupons.errors.save")),
				variant: "destructive",
			});
		}
	};

	const openCouponRedemptions = async (coupon: CouponRow) => {
		setRedemptionsCoupon(coupon);
		setRedemptionsOpen(true);
		setRedemptionsLoading(true);
		try {
			const data = await couponsAPI.redemptions(coupon.id, { page: 1, limit: 20 });
			setRedemptions(data.redemptions ?? []);
		} catch {
			setRedemptions([]);
			toast({
				title: t("common.error"),
				description: t("checkoutPromotions.coupons.errors.loadRedemptions"),
				variant: "destructive",
			});
		} finally {
			setRedemptionsLoading(false);
		}
	};

	// --- Gift card handlers ---
	const openGiftCreate = () => {
		setGiftForm({ code: generateGiftCode(), initialBalance: "", expiresAt: "" });
		setGiftDialogOpen(true);
	};

	const createGiftCard = async () => {
		const balance = Number(giftForm.initialBalance);
		if (!giftForm.code.trim() || balance <= 0) return;
		try {
			setGiftSaving(true);
			await giftCardsAPI.create({
				code: giftForm.code.trim(),
				initialBalance: balance,
				expiresAt: giftForm.expiresAt || undefined,
			});
			toast({ title: t("common.success"), description: t("checkoutPromotions.giftCards.created") });
			setGiftDialogOpen(false);
			setGiftForm(EMPTY_GIFT_FORM);
			await loadCards();
		} catch (error) {
			toast({
				title: t("common.error"),
				description: getApiErrorMessage(error, t, t("checkoutPromotions.giftCards.errors.save")),
				variant: "destructive",
			});
		} finally {
			setGiftSaving(false);
		}
	};

	const openGiftDetail = async (card: GiftCardRow) => {
		setSelectedCard(card);
		setGiftDetailOpen(true);
		setGiftDetailLoading(true);
		try {
			const data = await giftCardsAPI.getById(card.id);
			setSelectedCard(data.card ?? card);
			setGiftRedemptions(data.redemptions ?? []);
		} catch {
			toast({
				title: t("common.error"),
				description: t("checkoutPromotions.giftCards.errors.load"),
				variant: "destructive",
			});
		} finally {
			setGiftDetailLoading(false);
		}
	};

	const deactivateGiftCard = async (card: GiftCardRow) => {
		if (Number(card.balance) > 0) {
			toast({
				title: t("common.error"),
				description: t("checkoutPromotions.giftCards.errors.deactivateWithBalance"),
				variant: "destructive",
			});
			return;
		}
		try {
			await giftCardsAPI.update(card.id, { status: "inactive" });
			await loadCards();
		} catch (error) {
			toast({
				title: t("common.error"),
				description: getApiErrorMessage(error, t, t("checkoutPromotions.giftCards.errors.save")),
				variant: "destructive",
			});
		}
	};

	// --- Loyalty handlers ---
	const saveLoyalty = async () => {
		try {
			setLoyaltySaving(true);
			await loyaltyAPI.updateSettings({
				earnRatePerCurrency: loyalty.earnRatePerCurrency,
				redeemValuePerPoint: loyalty.redeemValuePerPoint,
				isActive: loyalty.isActive,
			});
			toast({ title: t("common.success"), description: t("checkoutPromotions.loyalty.saved") });
			await loadLoyalty();
		} catch (error) {
			toast({
				title: t("common.error"),
				description: getApiErrorMessage(error, t, t("checkoutPromotions.loyalty.errors.save")),
				variant: "destructive",
			});
		} finally {
			setLoyaltySaving(false);
		}
	};

	// --- Helpers ---
	const giftStatusLabel = (status: string) => {
		const key = `checkoutPromotions.giftCards.status.${status}`;
		const translated = t(key);
		return translated === key ? status : translated;
	};

	const txTypeLabel = (txType: string) => {
		const key = `checkoutPromotions.storeCredit.types.${txType}`;
		const translated = t(key);
		return translated === key ? txType : translated;
	};

	const formatCouponValue = (coupon: CouponRow) =>
		coupon.type === "percent" ? `${Number(coupon.value)}%` : formatNumber(Number(coupon.value));

	const formatDateRange = (from: string | null, to: string | null) => {
		if (!from && !to) return "—";
		const fromStr = from ? formatDate(from) : "…";
		const toStr = to ? formatDate(to) : "…";
		return `${fromStr} – ${toStr}`;
	};

	const filteredCoupons = useMemo(() => {
		const q = search.trim().toUpperCase();
		return coupons.filter(c => {
			if (q && !c.code.toUpperCase().includes(q)) return false;
			if (statusFilter === "active" && !c.isActive) return false;
			if (statusFilter === "inactive" && c.isActive) return false;
			return true;
		});
	}, [coupons, search, statusFilter]);

	const filteredCards = useMemo(() => {
		const q = search.trim().toUpperCase();
		return cards.filter(c => {
			if (q && !c.code.toUpperCase().includes(q)) return false;
			if (statusFilter !== "all" && c.status !== statusFilter) return false;
			return true;
		});
	}, [cards, search, statusFilter]);

	const activeCouponsCount = useMemo(() => coupons.filter(c => c.isActive).length, [coupons]);
	const activeCardsCount = useMemo(() => cards.filter(c => c.status === "active").length, [cards]);
	const outstandingGiftBalance = useMemo(
		() => cards.filter(c => c.status === "active").reduce((sum, c) => sum + Number(c.balance || 0), 0),
		[cards],
	);

	const typeLabels: Record<HubType, string> = {
		coupons: t("checkoutPromotions.coupons.title"),
		giftCards: t("checkoutPromotions.giftCards.title"),
		storeCredit: t("checkoutPromotions.storeCredit.title"),
		loyalty: t("checkoutPromotions.loyalty.title"),
	};

	const applyStoreCreditSearch = () => {
		const parsed = search.trim() ? parseInt(search.trim(), 10) : NaN;
		setAppliedCustomerId(Number.isFinite(parsed) && parsed > 0 ? parsed : undefined);
	};

	const searchPlaceholder =
		type === "coupons"
			? t("checkoutPromotions.hub.searchCoupons")
			: type === "giftCards"
				? t("checkoutPromotions.hub.searchGiftCards")
				: t("checkoutPromotions.storeCredit.filterCustomerPlaceholder");

	const contentLoading =
		(type === "coupons" && couponsLoading) ||
		(type === "giftCards" && cardsLoading) ||
		(type === "storeCredit" && txLoading) ||
		(type === "loyalty" && loyaltyLoading);

	return (
		<div className="space-y-4 p-4 md:p-6">
			<div>
				<PageHeading className="mb-1 ">{t("navigation.checkoutPromotions")}</PageHeading>
				<p className="text-muted-foreground text-sm">{t("checkoutPromotions.hub.description")}</p>
			</div>

			{/* Counters */}
			<div className="flex flex-wrap gap-2">
				{canCoupons && (
					<div className="flex items-center gap-2 rounded-xl border bg-card px-4 py-2.5">
						<span className={cn("h-2 w-2 rounded-full", TYPE_STYLES.coupons.dot)} />
						<span className="text-sm text-muted-foreground">
							{t("checkoutPromotions.hub.activeCoupons")}
						</span>
						<span className="text-sm font-semibold">{activeCouponsCount}</span>
					</div>
				)}
				{canGiftCards && (
					<>
						<div className="flex items-center gap-2 rounded-xl border bg-card px-4 py-2.5">
							<span className={cn("h-2 w-2 rounded-full", TYPE_STYLES.giftCards.dot)} />
							<span className="text-sm text-muted-foreground">
								{t("checkoutPromotions.hub.activeGiftCards")}
							</span>
							<span className="text-sm font-semibold">{activeCardsCount}</span>
						</div>
						<div className="flex items-center gap-2 rounded-xl border bg-card px-4 py-2.5">
							<span className={cn("h-2 w-2 rounded-full", TYPE_STYLES.giftCards.dot)} />
							<span className="text-sm text-muted-foreground">
								{t("checkoutPromotions.hub.outstandingGiftBalance")}
							</span>
							<span className="text-sm font-semibold">
								<Price value={outstandingGiftBalance} currency={currency} />
							</span>
						</div>
					</>
				)}
				{canLoyalty && !loyaltyLoading && (
					<div className="flex items-center gap-2 rounded-xl border bg-card px-4 py-2.5">
						<span
							className={cn(
								"h-2 w-2 rounded-full",
								loyalty.isActive ? TYPE_STYLES.loyalty.dot : "bg-muted-foreground/40",
							)}
						/>
						<span className="text-sm text-muted-foreground">{t("checkoutPromotions.loyalty.title")}</span>
						<span className="text-sm font-semibold">
							{loyalty.isActive ? t("common.active") : t("common.inactive")}
						</span>
					</div>
				)}
			</div>

			{/* Unified toolbar: type chips + contextual filters + contextual action */}
			<div className="flex flex-wrap items-center gap-2 rounded-xl border bg-card p-2">
				{visibleTypes.map(key => {
					const style = TYPE_STYLES[key];
					const Icon = style.icon;
					const isSelected = type === key;
					return (
						<button
							key={key}
							type="button"
							onClick={() => switchType(key)}
							className={cn(
								"flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-sm font-medium transition-colors",
								isSelected
									? style.active
									: "border-transparent text-muted-foreground hover:bg-muted/60",
							)}
						>
							<Icon className="h-4 w-4" />
							{typeLabels[key]}
						</button>
					);
				})}

				<div className="ms-auto flex flex-wrap items-center gap-2">
					{type !== "loyalty" && (
						<div className="relative w-full sm:w-auto">
							<Search className="absolute start-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
							<Input
								value={search}
								onChange={e => setSearch(e.target.value)}
								onKeyDown={e => {
									if (e.key === "Enter" && type === "storeCredit") applyStoreCreditSearch();
								}}
								onBlur={() => {
									if (type === "storeCredit") applyStoreCreditSearch();
								}}
								placeholder={searchPlaceholder}
								className="h-9 w-full sm:w-48 ps-8"
							/>
						</div>
					)}
					{(type === "coupons" || type === "giftCards") && (
						<Select value={statusFilter} onValueChange={setStatusFilter}>
							<SelectTrigger className="h-9 w-36">
								<SelectValue />
							</SelectTrigger>
							<SelectContent>
								<SelectItem value="all">{t("common.all")}</SelectItem>
								{type === "coupons" ? (
									<>
										<SelectItem value="active">{t("common.active")}</SelectItem>
										<SelectItem value="inactive">{t("common.inactive")}</SelectItem>
									</>
								) : (
									<>
										<SelectItem value="active">
											{t("checkoutPromotions.giftCards.status.active")}
										</SelectItem>
										<SelectItem value="inactive">
											{t("checkoutPromotions.giftCards.status.inactive")}
										</SelectItem>
										<SelectItem value="depleted">
											{t("checkoutPromotions.giftCards.status.depleted")}
										</SelectItem>
										<SelectItem value="expired">
											{t("checkoutPromotions.giftCards.status.expired")}
										</SelectItem>
									</>
								)}
							</SelectContent>
						</Select>
					)}
					{type === "coupons" && (
						<PermissionGate permission="cashierDiscount.create">
							<Button size="sm" className="h-9" onClick={openCouponCreate}>
								<Plus className="h-4 w-4 me-1" />
								{t("checkoutPromotions.coupons.create")}
							</Button>
						</PermissionGate>
					)}
					{type === "giftCards" && (
						<PermissionGate permission="cashierGiftCard.create">
							<Button size="sm" className="h-9" onClick={openGiftCreate}>
								<Plus className="h-4 w-4 me-1" />
								{t("checkoutPromotions.giftCards.issue")}
							</Button>
						</PermissionGate>
					)}
				</div>
			</div>

			{contentLoading ? (
				<Loading />
			) : (
				<>
					{/* Coupons */}
					{type === "coupons" &&
						(filteredCoupons.length === 0 ? (
							<div className="rounded-xl border">
								{search.trim() || statusFilter !== "all" ? (
									<EmptyState icon={Search} title={t("common.noResults")} />
								) : (
									<EmptyState
										icon={Tag}
										title={t("checkoutPromotions.coupons.empty")}
										action={
											canCreateCoupons
												? {
														label: t("checkoutPromotions.coupons.create"),
														onClick: openCouponCreate,
													}
												: undefined
										}
									/>
								)}
							</div>
						) : (
							<div className="rounded-xl border">
								<Table>
									<TableHeader>
										<TableRow>
											<TableHead>{t("checkoutPromotions.coupons.code")}</TableHead>
											<TableHead>{t("checkoutPromotions.coupons.type")}</TableHead>
											<TableHead>{t("checkoutPromotions.coupons.value")}</TableHead>
											<TableHead>{t("checkoutPromotions.coupons.branch")}</TableHead>
											<TableHead>{t("checkoutPromotions.coupons.validity")}</TableHead>
											<TableHead>{t("checkoutPromotions.coupons.uses")}</TableHead>
											<TableHead>{t("common.status")}</TableHead>
											<TableHead className="text-center">{t("common.actions")}</TableHead>
										</TableRow>
									</TableHeader>
									<TableBody>
										{filteredCoupons.map(coupon => (
											<TableRow key={coupon.id}>
												<TableCell>
													<Badge
														variant={coupon.isActive ? "success" : "warning"}
														className="font-mono font-bold"
													>
														{coupon.code}
													</Badge>
												</TableCell>
												<TableCell>
													{coupon.type === "percent"
														? t("checkoutPromotions.coupons.typePercent")
														: t("checkoutPromotions.coupons.typeFixed")}
												</TableCell>
												<TableCell>{formatCouponValue(coupon)}</TableCell>
												<TableCell>{branchLabel(coupon.branchId)}</TableCell>
												<TableCell className="text-sm">
													{formatDateRange(coupon.validFrom, coupon.validTo)}
												</TableCell>
												<TableCell>
													{coupon.usesCount}
													{coupon.maxUses != null ? ` / ${coupon.maxUses}` : ""}
												</TableCell>
												<TableCell>
													<Badge variant={coupon.isActive ? "success" : "secondary"}>
														{coupon.isActive ? t("common.active") : t("common.inactive")}
													</Badge>
												</TableCell>
												<TableCell className="text-center">
													<div className="flex items-center justify-center gap-2">
														<Button
															title={t("checkoutPromotions.coupons.redemptions")}
															aria-label={t("checkoutPromotions.coupons.redemptions")}
															variant="outline"
															size="sm"
															className="w-10 h-10"
															onClick={() => openCouponRedemptions(coupon)}
														>
															<History className="h-4 w-4" />
														</Button>
														<PermissionGate permission="cashierDiscount.update">
															<Button
																title={t("checkoutPromotions.coupons.edit")}
																aria-label={t("checkoutPromotions.coupons.edit")}
																variant="outline"
																size="sm"
																className="w-10 h-10"
																onClick={() => openCouponEdit(coupon)}
															>
																<Pencil className="h-4 w-4" />
															</Button>
															<Button
																variant="outline"
																size="sm"
																className="h-10"
																onClick={() => toggleCouponActive(coupon)}
															>
																{coupon.isActive
																	? t("checkoutPromotions.coupons.deactivate")
																	: t("checkoutPromotions.coupons.activate")}
															</Button>
														</PermissionGate>
													</div>
												</TableCell>
											</TableRow>
										))}
									</TableBody>
								</Table>
							</div>
						))}

					{/* Gift cards */}
					{type === "giftCards" &&
						(filteredCards.length === 0 ? (
							<div className="rounded-xl border">
								{search.trim() || statusFilter !== "all" ? (
									<EmptyState icon={Search} title={t("common.noResults")} />
								) : (
									<EmptyState
										icon={Gift}
										title={t("checkoutPromotions.giftCards.empty")}
										action={
											canIssueGiftCards
												? {
														label: t("checkoutPromotions.giftCards.issue"),
														onClick: openGiftCreate,
													}
												: undefined
										}
									/>
								)}
							</div>
						) : (
							<div className="rounded-xl border">
								<Table>
									<TableHeader>
										<TableRow>
											<TableHead>{t("checkoutPromotions.giftCards.code")}</TableHead>
											<TableHead>{t("checkoutPromotions.giftCards.initialBalance")}</TableHead>
											<TableHead>{t("checkoutPromotions.giftCards.balance")}</TableHead>
											<TableHead>{t("common.status")}</TableHead>
											<TableHead>{t("checkoutPromotions.giftCards.expiresAt")}</TableHead>
											<TableHead className="text-center">{t("common.actions")}</TableHead>
										</TableRow>
									</TableHeader>
									<TableBody>
										{filteredCards.map(card => (
											<TableRow key={card.id}>
												<TableCell>
													<Badge
														variant={card.status === "active" ? "success" : "warning"}
														className="font-mono font-bold"
													>
														{card.code}
													</Badge>
												</TableCell>
												<TableCell>
													<Price value={card.initialBalance} currency={currency} />
												</TableCell>
												<TableCell>
													<Price value={card.balance} currency={currency} />
												</TableCell>
												<TableCell>
													<Badge variant={card.status === "active" ? "success" : "secondary"}>
														{giftStatusLabel(card.status)}
													</Badge>
												</TableCell>
												<TableCell>
													{card.expiresAt ? formatDate(card.expiresAt) : "—"}
												</TableCell>
												<TableCell className="text-center">
													<div className="flex items-center justify-center gap-2">
														<Button
															title={t("checkoutPromotions.giftCards.viewDetail")}
															aria-label={t("checkoutPromotions.giftCards.viewDetail")}
															variant="outline"
															size="sm"
															onClick={() => openGiftDetail(card)}
														>
															<Eye className="h-4 w-4" />
														</Button>
														{card.status === "active" && (
															<PermissionGate permission="cashierGiftCard.update">
																<Button
																	variant="outline"
																	size="sm"
																	onClick={() => deactivateGiftCard(card)}
																>
																	{t("checkoutPromotions.giftCards.deactivate")}
																</Button>
															</PermissionGate>
														)}
													</div>
												</TableCell>
											</TableRow>
										))}
									</TableBody>
								</Table>
							</div>
						))}

					{/* Store credit */}
					{type === "storeCredit" &&
						(transactions.length === 0 ? (
							<p className="text-muted-foreground py-6 text-center text-sm">
								{t("checkoutPromotions.storeCredit.empty")}
							</p>
						) : (
							<div className="rounded-xl border">
								<Table>
									<TableHeader>
										<TableRow>
											<TableHead>{t("common.date")}</TableHead>
											<TableHead>{t("common.customer")}</TableHead>
											<TableHead>{t("checkoutPromotions.coupons.type")}</TableHead>
											<TableHead>{t("common.amount")}</TableHead>
											<TableHead>{t("checkoutPromotions.storeCredit.balanceAfter")}</TableHead>
											<TableHead>{t("common.notes")}</TableHead>
										</TableRow>
									</TableHeader>
									<TableBody>
										{transactions.map(tx => (
											<TableRow key={tx.id}>
												<TableCell>{formatDateTime(tx.createdAt)}</TableCell>
												<TableCell>
													<Link
														to={`/contacts?edit=${tx.customerId}`}
														className="text-primary hover:underline"
													>
														#{tx.customerId}
													</Link>
												</TableCell>
												<TableCell>
													<Badge variant="outline">{txTypeLabel(tx.type)}</Badge>
												</TableCell>
												<TableCell>
													<Price value={tx.amount} currency={currency} />
												</TableCell>
												<TableCell>
													<Price value={tx.balanceAfter} currency={currency} />
												</TableCell>
												<TableCell className="max-w-[200px] truncate">
													{tx.notes ?? "—"}
												</TableCell>
											</TableRow>
										))}
									</TableBody>
								</Table>
							</div>
						))}

					{/* Loyalty */}
					{type === "loyalty" && (
						<div className="max-w-2xl space-y-4">
							{planBlocked && (
								<div className="rounded-lg border border-warning/25 bg-warning/10 px-4 py-3 text-sm text-warning">
									{t("checkoutPromotions.loyalty.notOnPlan")}
								</div>
							)}
							<Card>
								<CardHeader>
									<CardTitle className="flex items-center gap-2 text-base">
										<Star className="h-5 w-5" />
										{t("checkoutPromotions.loyalty.title")}
									</CardTitle>
									<CardDescription>{t("checkoutPromotions.loyalty.description")}</CardDescription>
								</CardHeader>
								<CardContent className="space-y-6">
									<div className="space-y-2">
										<Label htmlFor="earnRate">{t("checkoutPromotions.loyalty.earnRate")}</Label>
										<Input
											id="earnRate"
											type="number"
											min={0}
											step={0.0001}
											value={loyalty.earnRatePerCurrency}
											onChange={e =>
												setLoyalty(s => ({
													...s,
													earnRatePerCurrency: Number(e.target.value) || 0,
												}))
											}
											disabled={planBlocked}
										/>
										<p className="text-xs text-muted-foreground">
											{t("checkoutPromotions.loyalty.earnRateHelp", { currency: currencyName })}
										</p>
									</div>
									<div className="space-y-2">
										<Label htmlFor="redeemValue">
											{t("checkoutPromotions.loyalty.redeemValue")}
										</Label>
										<Input
											id="redeemValue"
											type="number"
											min={0}
											step={0.0001}
											value={loyalty.redeemValuePerPoint}
											onChange={e =>
												setLoyalty(s => ({
													...s,
													redeemValuePerPoint: Number(e.target.value) || 0,
												}))
											}
											disabled={planBlocked}
										/>
										<p className="text-xs text-muted-foreground">
											{t("checkoutPromotions.loyalty.redeemValueHelp", {
												currency: currencyName,
											})}
										</p>
									</div>
									<div className="flex items-center gap-2">
										<Switch
											aria-label={t("checkoutPromotions.loyalty.active")}
											checked={loyalty.isActive}
											onCheckedChange={v => setLoyalty(s => ({ ...s, isActive: v }))}
											disabled={planBlocked}
										/>
										<Label>{t("checkoutPromotions.loyalty.active")}</Label>
									</div>
									<PermissionGate permission="cashierLoyalty.update">
										<Button onClick={saveLoyalty} disabled={loyaltySaving || planBlocked}>
											{loyaltySaving ? t("common.loading") : t("common.save")}
										</Button>
									</PermissionGate>
								</CardContent>
							</Card>
						</div>
					)}
				</>
			)}

			{/* Coupon create/edit dialog */}
			<Dialog
				open={couponDialogOpen}
				onOpenChange={open => {
					setCouponDialogOpen(open);
					if (!open) {
						setEditingCouponId(null);
						setCouponForm(EMPTY_COUPON_FORM);
					}
				}}
			>
				<DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
					<DialogHeader>
						<DialogTitle>
							{editingCouponId != null
								? t("checkoutPromotions.coupons.edit")
								: t("checkoutPromotions.coupons.create")}
						</DialogTitle>
					</DialogHeader>
					<div className="space-y-4 py-2">
						<div className="space-y-2">
							<Label>{t("checkoutPromotions.coupons.code")}</Label>
							<Input
								aria-label={t("checkoutPromotions.coupons.code")}
								value={couponForm.code}
								onChange={e => setCouponForm(f => ({ ...f, code: e.target.value.toUpperCase() }))}
								placeholder="WELCOME10"
								readOnly={editingCouponId != null}
								className={editingCouponId != null ? "bg-muted" : undefined}
							/>
						</div>
						<div className="grid grid-cols-2 gap-4">
							<div className="space-y-2">
								<Label>{t("checkoutPromotions.coupons.type")}</Label>
								<Select
									value={couponForm.type}
									onValueChange={v => setCouponForm(f => ({ ...f, type: v as "percent" | "fixed" }))}
								>
									<SelectTrigger aria-label={t("checkoutPromotions.coupons.type")}>
										<SelectValue />
									</SelectTrigger>
									<SelectContent>
										<SelectItem value="percent">
											{t("checkoutPromotions.coupons.typePercent")}
										</SelectItem>
										<SelectItem value="fixed">
											{t("checkoutPromotions.coupons.typeFixed")}
										</SelectItem>
									</SelectContent>
								</Select>
							</div>
							<div className="space-y-2">
								<Label>{t("checkoutPromotions.coupons.value")}</Label>
								<Input
									aria-label={t("checkoutPromotions.coupons.value")}
									type="number"
									min={0}
									value={couponForm.value}
									onChange={e => setCouponForm(f => ({ ...f, value: e.target.value }))}
								/>
							</div>
						</div>
						<div className="grid grid-cols-2 gap-4">
							<div className="space-y-2">
								<Label>{t("checkoutPromotions.coupons.minOrder")}</Label>
								<Input
									aria-label={t("checkoutPromotions.coupons.minOrder")}
									type="number"
									min={0}
									value={couponForm.minOrderAmount}
									onChange={e => setCouponForm(f => ({ ...f, minOrderAmount: e.target.value }))}
								/>
							</div>
							<div className="space-y-2">
								<Label>{t("checkoutPromotions.coupons.maxUses")}</Label>
								<Input
									aria-label={t("checkoutPromotions.coupons.maxUses")}
									type="number"
									min={1}
									value={couponForm.maxUses}
									onChange={e => setCouponForm(f => ({ ...f, maxUses: e.target.value }))}
									placeholder={t("common.optional")}
								/>
							</div>
						</div>
						<div className="grid grid-cols-2 gap-4">
							<div className="space-y-2">
								<Label>{t("checkoutPromotions.coupons.perCustomerLimit")}</Label>
								<Input
									aria-label={t("checkoutPromotions.coupons.perCustomerLimit")}
									type="number"
									min={1}
									value={couponForm.perCustomerLimit}
									onChange={e => setCouponForm(f => ({ ...f, perCustomerLimit: e.target.value }))}
									placeholder={t("common.optional")}
								/>
							</div>
							<div className="space-y-2">
								<Label>{t("checkoutPromotions.coupons.branch")}</Label>
								<Select
									value={couponForm.branchId || "all"}
									onValueChange={v => setCouponForm(f => ({ ...f, branchId: v === "all" ? "" : v }))}
								>
									<SelectTrigger aria-label={t("checkoutPromotions.coupons.branch")}>
										<SelectValue />
									</SelectTrigger>
									<SelectContent>
										<SelectItem value="all">
											{t("checkoutPromotions.coupons.allBranches")}
										</SelectItem>
										{branches.map(branch => (
											<SelectItem key={branch.id} value={String(branch.id)}>
												{i18n.language === "ar" ? branch.name : branch.nameEn || branch.name}
											</SelectItem>
										))}
									</SelectContent>
								</Select>
							</div>
						</div>
						<div className="grid grid-cols-2 gap-4">
							<div className="space-y-2">
								<Label>{t("checkoutPromotions.coupons.validFrom")}</Label>
								<Input
									aria-label={t("checkoutPromotions.coupons.validFrom")}
									type="date"
									value={couponForm.validFrom}
									onChange={e => setCouponForm(f => ({ ...f, validFrom: e.target.value }))}
								/>
							</div>
							<div className="space-y-2">
								<Label>{t("checkoutPromotions.coupons.validTo")}</Label>
								<Input
									aria-label={t("checkoutPromotions.coupons.validTo")}
									type="date"
									value={couponForm.validTo}
									onChange={e => setCouponForm(f => ({ ...f, validTo: e.target.value }))}
								/>
							</div>
						</div>
						<div className="flex items-center gap-2">
							<Switch
								aria-label={t("common.active")}
								checked={couponForm.isActive}
								onCheckedChange={v => setCouponForm(f => ({ ...f, isActive: v }))}
							/>
							<Label>{t("common.active")}</Label>
						</div>
					</div>
					<DialogFooter>
						<Button variant="outline" onClick={() => setCouponDialogOpen(false)}>
							{t("common.cancel")}
						</Button>
						<Button onClick={saveCoupon} disabled={couponSaving || !couponForm.code.trim()}>
							{couponSaving ? t("common.loading") : t("common.save")}
						</Button>
					</DialogFooter>
				</DialogContent>
			</Dialog>

			{/* Coupon redemptions dialog */}
			<Dialog open={redemptionsOpen} onOpenChange={setRedemptionsOpen}>
				<DialogContent className="max-w-lg">
					<DialogHeader>
						<DialogTitle>
							{t("checkoutPromotions.coupons.redemptions")}{" "}
							{redemptionsCoupon ? `— ${redemptionsCoupon.code}` : ""}
						</DialogTitle>
					</DialogHeader>
					{redemptionsLoading ? (
						<Loading />
					) : redemptions.length === 0 ? (
						<p className="text-sm text-muted-foreground">{t("checkoutPromotions.coupons.noRedemptions")}</p>
					) : (
						<Table>
							<TableHeader>
								<TableRow>
									<TableHead>{t("common.date")}</TableHead>
									<TableHead>{t("checkoutPromotions.coupons.sale")}</TableHead>
									<TableHead>{t("common.customer")}</TableHead>
									<TableHead>{t("common.amount")}</TableHead>
								</TableRow>
							</TableHeader>
							<TableBody>
								{redemptions.map(row => (
									<TableRow key={row.id}>
										<TableCell>{formatDateTime(row.createdAt)}</TableCell>
										<TableCell>{row.saleId ?? "—"}</TableCell>
										<TableCell>{row.customerId ?? "—"}</TableCell>
										<TableCell>
											<Price value={row.amountApplied} currency={currency} />
										</TableCell>
									</TableRow>
								))}
							</TableBody>
						</Table>
					)}
				</DialogContent>
			</Dialog>

			{/* Gift card issue dialog */}
			<Dialog open={giftDialogOpen} onOpenChange={setGiftDialogOpen}>
				<DialogContent>
					<DialogHeader>
						<DialogTitle>{t("checkoutPromotions.giftCards.issue")}</DialogTitle>
					</DialogHeader>
					<div className="space-y-4 py-2">
						<div className="space-y-2">
							<Label>{t("checkoutPromotions.giftCards.code")}</Label>
							<Input
								aria-label={t("checkoutPromotions.giftCards.code")}
								value={giftForm.code}
								onChange={e => setGiftForm(f => ({ ...f, code: e.target.value.toUpperCase() }))}
							/>
						</div>
						<div className="space-y-2">
							<Label>{t("checkoutPromotions.giftCards.initialBalance")}</Label>
							<Input
								aria-label={t("checkoutPromotions.giftCards.initialBalance")}
								type="number"
								min={0.01}
								step={0.01}
								value={giftForm.initialBalance}
								onChange={e => setGiftForm(f => ({ ...f, initialBalance: e.target.value }))}
							/>
						</div>
						<div className="space-y-2">
							<Label>{t("checkoutPromotions.giftCards.expiresAt")}</Label>
							<Input
								aria-label={t("checkoutPromotions.giftCards.expiresAt")}
								type="date"
								value={giftForm.expiresAt}
								onChange={e => setGiftForm(f => ({ ...f, expiresAt: e.target.value }))}
							/>
						</div>
					</div>
					<DialogFooter>
						<Button variant="outline" onClick={() => setGiftDialogOpen(false)}>
							{t("common.cancel")}
						</Button>
						<Button
							onClick={createGiftCard}
							disabled={giftSaving || !giftForm.code.trim() || Number(giftForm.initialBalance) <= 0}
						>
							{giftSaving ? t("common.creating") : t("common.create")}
						</Button>
					</DialogFooter>
				</DialogContent>
			</Dialog>

			{/* Gift card detail dialog */}
			<Dialog open={giftDetailOpen} onOpenChange={setGiftDetailOpen}>
				<DialogContent className="max-w-lg">
					<DialogHeader>
						<DialogTitle>{t("checkoutPromotions.giftCards.viewDetail")}</DialogTitle>
					</DialogHeader>
					{giftDetailLoading || !selectedCard ? (
						<Loading />
					) : (
						<div className="space-y-4">
							<div className="grid grid-cols-2 gap-3 text-sm">
								<div>
									<p className="text-muted-foreground">{t("checkoutPromotions.giftCards.code")}</p>
									<p className="font-mono font-medium">{selectedCard.code}</p>
								</div>
								<div>
									<p className="text-muted-foreground">{t("common.status")}</p>
									<Badge variant={selectedCard.status === "active" ? "success" : "secondary"}>
										{giftStatusLabel(selectedCard.status)}
									</Badge>
								</div>
								<div>
									<p className="text-muted-foreground">{t("checkoutPromotions.giftCards.balance")}</p>
									<p>
										<Price value={selectedCard.balance} currency={currency} />
									</p>
								</div>
								<div>
									<p className="text-muted-foreground">
										{t("checkoutPromotions.giftCards.expiresAt")}
									</p>
									<p>{selectedCard.expiresAt ? formatDate(selectedCard.expiresAt) : "—"}</p>
								</div>
							</div>
							<div>
								<p className="text-sm font-medium mb-2">
									{t("checkoutPromotions.giftCards.redemptions")}
								</p>
								{giftRedemptions.length === 0 ? (
									<p className="text-sm text-muted-foreground">
										{t("checkoutPromotions.giftCards.noRedemptions")}
									</p>
								) : (
									<Table>
										<TableHeader>
											<TableRow>
												<TableHead>{t("common.date")}</TableHead>
												<TableHead>{t("checkoutPromotions.coupons.sale")}</TableHead>
												<TableHead>{t("common.amount")}</TableHead>
											</TableRow>
										</TableHeader>
										<TableBody>
											{giftRedemptions.map(row => (
												<TableRow key={row.id}>
													<TableCell>{formatDateTime(row.createdAt)}</TableCell>
													<TableCell>{row.saleId ?? "—"}</TableCell>
													<TableCell>
														<Price value={row.amount} currency={currency} />
													</TableCell>
												</TableRow>
											))}
										</TableBody>
									</Table>
								)}
							</div>
						</div>
					)}
				</DialogContent>
			</Dialog>
		</div>
	);
}
